import { Tooltip } from "@smartsuite/react-ui/lib";
import React, { FunctionComponent, memo, useCallback, useMemo, useState } from "react";
import { EFitOptions } from "filestack-js";
import cx from "classnames";
import { File } from "@smartsuite/types";

import { isFileRenderable } from "../../../utils/filestack";

import { FilestackImage } from "../FilestackImage/FilestackImage";
import { FilePreviewIcon } from "../FilePreviewIcon/FilePreviewIcon";

import "./FilePreview.sass";

interface FilePreviewProps {
  file: File;
  width: number;
  height: number;
  className?: string;
  isShowTooltip?: boolean;
  showIcon?: boolean;
  showLoader?: boolean;
  onClick?: (event: React.MouseEvent) => void;
}

const FilePreviewRaw: FunctionComponent<FilePreviewProps> = ({
  file,
  width,
  height,
  className,
  isShowTooltip = true,
  showIcon,
  showLoader = true,
  onClick,
}): JSX.Element => {
  const isRenderable = useMemo(() => isFileRenderable(file) && !showIcon, [file, showIcon]);

  const previewTransform = useMemo(
    () => ({
      resize: {
        width: width * 2,
        height: height * 2,
        fit: EFitOptions.crop,
      },
      rotate: {
        deg: "exif",
      },
    }),
    [height, width]
  );

  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = useCallback(() => {
    if (!isShowTooltip) return;
    setShowTooltip(true);
  }, [isShowTooltip]);

  const handleMouseLeave = useCallback(() => {
    if (!isShowTooltip) return;
    setShowTooltip(false);
  }, [isShowTooltip]);

  const classes = cx("file-preview", className);

  const preview = isRenderable ? (
    <FilestackImage
      file={file}
      width={width}
      height={height}
      transformOptions={previewTransform}
      showLoader={showLoader}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
    />
  ) : (
    <FilePreviewIcon
      file={file}
      dimension={{ width, height }}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  );

  if (showTooltip) {
    return (
      <Tooltip
        className={classes}
        ariaLabel="File Preview"
        padding={2}
        text={file.metadata.filename}
        target={preview}
      />
    );
  }

  return (
    <div className={classes} aria-label="File Preview">
      {preview}
    </div>
  );
};

export const FilePreview = memo(FilePreviewRaw);
